// src/utils/firebase.js
import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc as firestoreAddDoc, getDocs, getDoc, setDoc as firestoreSetDoc, updateDoc as firestoreUpdateDoc, query, where, deleteDoc as firestoreDeleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, applyActionCode, checkActionCode } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({ prompt: 'select_account' });

export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const signInWithGoogleRedirect = () => signInWithRedirect(auth, googleProvider);
export const storage = getStorage(app);

export { serverTimestamp, applyActionCode, checkActionCode };

// ================================
// TRIAL BLOCKING SYSTEM
// ================================

let trialExpiredCallback = null;
let clubDataCache = null;

export const setTrialExpiredCallback = (callback) => {
  trialExpiredCallback = callback;
};

export const updateClubDataCache = (clubData) => {
  clubDataCache = clubData;
};

const canWrite = () => {
  if (!clubDataCache) {
    console.warn('⚠️ No club data available for trial check');
    return true;
  }

  const now = new Date();
  const trialEndsAt = clubDataCache.trialEndsAt?.toDate ? 
    clubDataCache.trialEndsAt.toDate() : 
    new Date(clubDataCache.trialEndsAt);
  
  const subscriptionStatus = clubDataCache.subscriptionStatus || 'trial';
  const msRemaining = trialEndsAt - now;
  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
  
  const isInTrial = subscriptionStatus === 'trial' && daysRemaining > 0;
  const isActive = subscriptionStatus === 'active';
  
  return isInTrial || isActive;
};

const blockOperation = (operationName) => {
  console.log(`🚫 Trial expired - blocking ${operationName}`);
  if (trialExpiredCallback) {
    trialExpiredCallback();
  }
  return Promise.reject(new Error('TRIAL_EXPIRED'));
};

export const setDoc = async (...args) => {
  if (!canWrite()) return blockOperation('setDoc');
  return firestoreSetDoc(...args);
};

export const updateDoc = async (...args) => {
  if (!canWrite()) return blockOperation('updateDoc');
  return firestoreUpdateDoc(...args);
};

export const deleteDoc = async (...args) => {
  if (!canWrite()) return blockOperation('deleteDoc');
  return firestoreDeleteDoc(...args);
};

export const addDoc = async (...args) => {
  if (!canWrite()) return blockOperation('addDoc');
  return firestoreAddDoc(...args);
};

export { doc, getDoc, collection, getDocs, query, where };

// ================================
// FIREBASE STORAGE HELPER FUNCTIONS
// ================================

export const getFirebaseImageUrl = async (imagePath) => {
  try {
    if (!imagePath) return null;
    const imageRef = ref(storage, imagePath);
    const downloadURL = await getDownloadURL(imageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error getting Firebase image URL:', error);
    throw error;
  }
};

export const isFirebaseStorageUrl = (url) => {
  return url && url.includes('firebasestorage.googleapis.com');
};

// ================================
// USER PROFILE FUNCTIONS
// ================================

export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    return userDoc.exists() ? userDoc.data() : null;
  } catch (err) {
    console.error("Error getting user profile:", err);
    return null;
  }
};

export const createUserProfile = async (userId, email, clubId, role = 'member', firstName = '', surname = '', mobile = '') => {
  try {
    let finalRole;
    if (role === 'admin') {
      finalRole = 'admin';
    } else if (role === 'member') {
      finalRole = 'member';
    } else {
      finalRole = 'member';
    }
    
    const trialEndsAt = new Date();
    trialEndsAt.setDate(trialEndsAt.getDate() + 7);
    
    const userData = {
      email: email,
      clubId: clubId,
      role: finalRole,
      createdAt: Date.now(),
      hasCompletedSetup: false,
      status: 'active',
      lastLogin: new Date().toISOString(),
      trialEndsAt: trialEndsAt.toISOString(),
      subscriptionStatus: 'trial',
      subscriptionTier: null
    };
    
    if (firstName) userData.firstName = firstName;
    if (surname) userData.surname = surname;
    if (firstName && surname) userData.fullName = `${firstName} ${surname}`.trim();
    if (mobile) userData.mobile = mobile;
    
    await setDoc(doc(db, 'users', userId), userData);
    console.log("✅ User profile created successfully with role:", finalRole);
    return userData;
  } catch (err) {
    console.error("❌ Error creating user profile:", err);
    throw err;
  }
};

// ================================
// SHARING FUNCTIONS
// ================================

export const createShareableLink = async (allocatorType, allocations, date, clubId, clubName) => {
  try {
    const shareId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const shareData = {
      type: allocatorType,
      allocations: allocations,
      date: date,
      clubId: clubId,
      clubName: clubName || 'Unknown Club',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    };
    await setDoc(doc(db, 'sharedAllocations', shareId), shareData);
    const shareUrl = `${window.location.origin}/share/${shareId}`;
    return { url: shareUrl, shareId: shareId };
  } catch (error) {
    console.error('Error creating shareable link:', error);
    throw error;
  }
};

export const createSharedAllocation = async (shareId, shareData) => {
  try {
    const shareRef = doc(db, 'sharedAllocations', shareId);
    await setDoc(shareRef, shareData);
    return shareId;
  } catch (error) {
    console.error('Error creating shared allocation:', error);
    throw error;
  }
};

export const getSharedAllocation = async (shareId) => {
  try {
    const docRef = doc(db, 'sharedAllocations', shareId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (new Date(data.expiresAt) < new Date()) {
        throw new Error('This share link has expired');
      }
      return data;
    } else {
      throw new Error('Share link not found');
    }
  } catch (error) {
    console.error('Error retrieving shared allocation:', error);
    throw error;
  }
};

// ================================
// CLUB FUNCTIONS
// ================================

export const createClub = async (clubName, createdBy, clubId, firstName = '', surname = '', mobile = '') => {
  try {
    const finalClubId = clubId || Math.random().toString(36).substr(2, 6).toUpperCase();
    const trialEndsAt = new Date();
    trialEndsAt.setDate(trialEndsAt.getDate() + 7);
    
    const clubData = {
      clubId: finalClubId,
      name: clubName,
      code: finalClubId,
      createdBy: createdBy || auth.currentUser?.uid,
      createdAt: Date.now(),
      subscriptionStatus: 'trial',
      trialEndsAt: trialEndsAt.toISOString(),
      subscriptionTier: null
    };
    
    if (firstName) clubData.adminFirstName = firstName;
    if (surname) clubData.adminSurname = surname;
    if (mobile) clubData.adminMobile = mobile;
    
    await setDoc(doc(db, 'clubs', finalClubId), clubData);
    console.log('✅ Club created successfully with ID:', finalClubId);
    return finalClubId;
  } catch (err) {
    console.error('❌ Error creating club:', err);
    throw err;
  }
};

export const getClubData = async (clubId) => {
  try {
    const clubDoc = await getDoc(doc(db, 'clubs', clubId));
    if (clubDoc.exists()) {
      return clubDoc.data();
    }
    return null;
  } catch (err) {
    console.error("Error getting club data:", err);
    return null;
  }
};

export const testFirebaseConnection = async () => {
  try {
    console.log("Testing Firebase connection...");
    const testCollection = collection(db, 'connectionTest');
    await getDocs(testCollection);
    console.log("Firebase connection successful!");
    return true;
  } catch (err) {
    console.error("Firebase connection failed:", err);
    return false;
  }
};
